/**
 * ShareDocumentModal Component
 * GAP-023: Add Share action to document cards
 * IMPL-JM-002 Phase 1: Share Entry Points / Phase 2: Share Configuration Flow
 *
 * Simplified share modal for sharing individual or multiple documents
 * with existing delegates or new recipients.
 *
 * Connected to real API endpoints:
 * - POST /api/v1/access-grants - Create access grant
 * - GET /api/v1/access-grants/grantees - Recent grantees for autocomplete
 */

import { useState, useEffect, type ChangeEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Modal,
  Button,
  Input,
  Select,
  Textarea,
  DatePicker,
  Badge,
  Icon,
  useToast,
  formatDateForInput,
} from '@myvault/shared/components';
import {
  accessGrantsApi,
  type GrantRelationship,
  type GrantPermission,
  type RecentGrantee,
} from '@myvault/shared/api';
import type { Document } from '@myvault/shared';

interface ShareDocumentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Single document to share */
  documentId?: string;
  /** Multiple documents for bulk share */
  documentIds?: string[];
  /** Document details for display */
  document?: Document;
  /** Pre-selected recipient (from existing grants) */
  preSelectedGrantee?: string;
}

// Map frontend relationship types to backend types
type RelationshipType = 'partner' | 'family' | 'professional' | 'friend' | 'other';
type PermissionLevel = 'view_only' | 'view_download' | 'full_access';

// Frontend to backend relationship mapping
const RELATIONSHIP_MAP: Record<RelationshipType, GrantRelationship> = {
  partner: 'spouse',
  family: 'sibling', // Use sibling as default for family
  professional: 'advisor',
  friend: 'other',
  other: 'other',
};

// Backend to frontend relationship mapping (for displaying recent grantees)
const RELATIONSHIP_REVERSE_MAP: Record<GrantRelationship, RelationshipType> = {
  spouse: 'partner',
  child: 'family',
  parent: 'family',
  sibling: 'family',
  advisor: 'professional',
  accountant: 'professional',
  other: 'other',
};

const RELATIONSHIP_OPTIONS = [
  { value: 'partner', label: 'Partner/Echtgenoot' },
  { value: 'family', label: 'Familielid' },
  { value: 'professional', label: 'Professionele Gemachtigde' },
  { value: 'friend', label: 'Vertrouwenspersoon' },
  { value: 'other', label: 'Anders' },
];

/**
 * GAP-025: Permission options with detailed explanations and examples
 */
const PERMISSION_OPTIONS = [
  {
    value: 'view_only',
    label: 'Alleen Bekijken',
    description: 'Kan het document bekijken in de browser, maar niet downloaden of opslaan.',
    examples: ['Perfect voor: tijdelijke inzage, gevoelige documenten'],
    icon: 'eye',
    badge: 'Beperkt',
    badgeColor: 'bg-blue-100 text-blue-800',
  },
  {
    value: 'view_download',
    label: 'Bekijken & Downloaden',
    description: 'Kan het document bekijken én een kopie downloaden naar eigen apparaat.',
    examples: ['Perfect voor: accountant die belastingdocumenten nodig heeft'],
    icon: 'arrowDownTray',
    badge: 'Standaard',
    badgeColor: 'bg-green-100 text-green-800',
    recommended: true,
  },
  {
    value: 'full_access',
    label: 'Volledige Toegang',
    description: 'Kan bekijken, downloaden, metagegevens bewerken en met anderen delen.',
    examples: ['Perfect voor: partner met volledige kluis-toegang'],
    icon: 'key',
    badge: 'Volledig',
    badgeColor: 'bg-amber-100 text-amber-800',
  },
];

const EXPIRY_PRESETS = [
  { value: '', label: 'Geen vervaldatum' },
  { value: '7', label: '1 week' },
  { value: '30', label: '1 maand' },
  { value: '90', label: '3 maanden' },
  { value: '365', label: '1 jaar' },
  { value: 'custom', label: 'Aangepaste datum' },
];

/**
 * Hook to fetch recent grantees for autocomplete suggestions.
 * Connected to GET /api/v1/access-grants/grantees
 */
function useRecentGrantees() {
  return useQuery({
    queryKey: ['delegation', 'recent-grantees'],
    queryFn: async (): Promise<RecentGrantee[]> => {
      return accessGrantsApi.getRecentGrantees(5);
    },
    // Stale time of 5 minutes - suggestions don't need to be super fresh
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Map frontend permission level to backend permission array.
 */
function mapPermissionToGrants(permission: PermissionLevel): GrantPermission[] {
  switch (permission) {
    case 'view_only':
      return ['view'];
    case 'view_download':
      return ['view', 'download'];
    case 'full_access':
      return ['view', 'download', 'annotate', 'share', 'export'];
    default:
      return ['view'];
  }
}

/**
 * Hook to create an access grant (share documents).
 * Connected to POST /api/v1/access-grants
 */
function useShareDocument() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: {
      documentIds: string[];
      email: string;
      relationship: RelationshipType;
      permission: PermissionLevel;
      expiryDate: string | null;
      message: string;
    }) => {
      // Convert frontend permission level to backend permissions array
      const permissions = mapPermissionToGrants(data.permission);

      // Convert frontend relationship to backend relationship type
      const backendRelationship = RELATIONSHIP_MAP[data.relationship];

      // Determine scope based on document selection
      const scope: 'document' | 'all' = data.documentIds.length > 0 ? 'document' : 'all';
      const scopeFilter = data.documentIds.length > 0
        ? { documentIds: data.documentIds }
        : undefined;

      return accessGrantsApi.create({
        granteeEmail: data.email,
        relationship: backendRelationship,
        scope,
        scopeFilter,
        permissions,
        validTo: data.expiryDate,
        invitationMessage: data.message || undefined,
      });
    },
    onSuccess: () => {
      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['delegation'] });
      queryClient.invalidateQueries({ queryKey: ['access-grants'] });
      toast.success(
        'Uitnodiging verstuurd',
        'De ontvanger krijgt een e-mail met een link om toegang te krijgen.'
      );
    },
    onError: (error) => {
      toast.error(
        'Delen mislukt',
        error instanceof Error ? error.message : 'Probeer het opnieuw.'
      );
    },
  });
}

export function ShareDocumentModal({
  open,
  onOpenChange,
  documentId,
  documentIds,
  document,
  preSelectedGrantee,
}: ShareDocumentModalProps) {
  // Form state
  const [email, setEmail] = useState(preSelectedGrantee || '');
  const [selectedGrantee, setSelectedGrantee] = useState<RecentGrantee | null>(null);
  const [relationship, setRelationship] = useState<RelationshipType>('other');
  const [permission, setPermission] = useState<PermissionLevel>('view_download');
  const [expiryPreset, setExpiryPreset] = useState('');
  const [customExpiryDate, setCustomExpiryDate] = useState('');
  const [message, setMessage] = useState('');
  const [showEmailSuggestions, setShowEmailSuggestions] = useState(false);

  // Data fetching
  const { data: recentGrantees } = useRecentGrantees();
  const shareDocument = useShareDocument();

  // Calculate document IDs
  const allDocumentIds = documentIds || (documentId ? [documentId] : []);
  const documentCount = allDocumentIds.length;

  // Filter recent grantees based on email input
  const filteredSuggestions =
    recentGrantees?.filter(
      (g) =>
        g.email.toLowerCase().includes(email.toLowerCase()) ||
        g.name.toLowerCase().includes(email.toLowerCase())
    ) || [];

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setEmail(preSelectedGrantee || '');
      setSelectedGrantee(null);
      setRelationship('other');
      setPermission('view_download');
      setExpiryPreset('');
      setCustomExpiryDate('');
      setMessage('');
    }
  }, [open, preSelectedGrantee]);

  // Handle grantee selection from suggestions
  const handleSelectGrantee = (grantee: RecentGrantee) => {
    setSelectedGrantee(grantee);
    setEmail(grantee.email);
    // Convert backend relationship type to frontend type
    const frontendRelationship = RELATIONSHIP_REVERSE_MAP[grantee.relationship] || 'other';
    setRelationship(frontendRelationship);
    setShowEmailSuggestions(false);
  };

  // Calculate expiry date
  const getExpiryDate = (): string | null => {
    if (expiryPreset === 'custom' && customExpiryDate) {
      return new Date(customExpiryDate).toISOString();
    }
    if (expiryPreset && expiryPreset !== 'custom') {
      const days = parseInt(expiryPreset, 10);
      const date = new Date();
      date.setDate(date.getDate() + days);
      return date.toISOString();
    }
    return null;
  };

  // Handle form submission
  const handleSubmit = () => {
    if (!email.trim() || allDocumentIds.length === 0) return;

    shareDocument.mutate(
      {
        documentIds: allDocumentIds,
        email: email.trim(),
        relationship,
        permission,
        expiryDate: getExpiryDate(),
        message: message.trim(),
      },
      {
        onSuccess: () => {
          onOpenChange(false);
        },
      }
    );
  };

  // Validate email format
  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const canSubmit = isValidEmail && allDocumentIds.length > 0 && !shareDocument.isPending;

  // Modal title
  const modalTitle = documentCount > 1 ? `${documentCount} documenten delen` : 'Document delen';

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={modalTitle}
      size="lg"
    >
      <div className="space-y-6">
        {/* Document Info */}
        {document && (
          <div className="flex items-center gap-3 p-3 bg-mv-gray-50 rounded-lg">
            <Icon name="document" className="w-8 h-8 text-mv-gray-400" />
            <div className="min-w-0 flex-1">
              <p className="font-medium text-mv-gray-900 truncate">{document.title}</p>
              <p className="text-sm text-mv-gray-500">
                {document.category || 'Geen categorie'}
              </p>
            </div>
          </div>
        )}

        {/* Multiple documents info */}
        {documentCount > 1 && (
          <div className="flex items-center gap-3 p-3 bg-mv-gray-50 rounded-lg">
            <Icon name="documentDuplicate" className="w-8 h-8 text-mv-gray-400" />
            <div>
              <p className="font-medium text-mv-gray-900">
                {documentCount} documenten geselecteerd
              </p>
              <p className="text-sm text-mv-gray-500">
                Alle documenten worden gedeeld met dezelfde instellingen
              </p>
            </div>
          </div>
        )}

        {/* GAP-024: Email Input with Enhanced Suggestions */}
        <div className="relative">
          <label className="block text-sm font-medium text-mv-gray-700 mb-2">
            E-mailadres van ontvanger
          </label>
          <div className="relative">
            <Icon name="envelope" className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-mv-gray-400" />
            <Input
              type="email"
              value={email}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setEmail(e.target.value);
                setSelectedGrantee(null);
                setShowEmailSuggestions(true);
              }}
              onFocus={() => setShowEmailSuggestions(true)}
              onBlur={() => setTimeout(() => setShowEmailSuggestions(false), 200)}
              placeholder="naam@voorbeeld.nl"
              aria-label="E-mailadres van ontvanger"
              className="pl-10"
            />
          </div>

          {/* GAP-024: Recent Grantees Suggestions with relationship context */}
          {showEmailSuggestions && filteredSuggestions.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-mv-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
              <div className="px-3 py-2 bg-mv-gray-50 border-b border-mv-gray-200">
                <p className="text-xs font-medium text-mv-gray-600 flex items-center gap-1">
                  <Icon name="clock" className="w-3 h-3" />
                  Recente ontvangers
                </p>
              </div>
              {filteredSuggestions.map((grantee) => (
                <button
                  key={grantee.id}
                  type="button"
                  className="w-full px-3 py-3 text-left hover:bg-mv-blue-50 flex items-center gap-3 border-b border-mv-gray-100 last:border-0 transition-colors"
                  onClick={() => handleSelectGrantee(grantee)}
                >
                  <div className="flex-shrink-0 w-10 h-10 bg-mv-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-sm font-semibold text-mv-blue-700">
                      {grantee.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-mv-gray-900 truncate">
                      {grantee.name}
                    </p>
                    <p className="text-xs text-mv-gray-500 truncate">{grantee.email}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="secondary" className="text-xs py-0.5">
                        {/* Convert backend relationship type to frontend label */}
                        {RELATIONSHIP_OPTIONS.find(r => r.value === RELATIONSHIP_REVERSE_MAP[grantee.relationship])?.label || grantee.relationship}
                      </Badge>
                      <span className="text-xs text-mv-gray-400">
                        {grantee.activeGrants} document{grantee.activeGrants !== 1 ? 'en' : ''} gedeeld
                      </span>
                    </div>
                  </div>
                  <Icon name="arrowRight" className="w-4 h-4 text-mv-gray-400" />
                </button>
              ))}
            </div>
          )}

          {/* GAP-024: No suggestions but show tip */}
          {showEmailSuggestions && email.length > 0 && filteredSuggestions.length === 0 && (
            <div className="absolute z-10 w-full mt-1 bg-mv-gray-50 border border-mv-gray-200 rounded-lg p-3">
              <p className="text-xs text-mv-gray-500 flex items-center gap-2">
                <Icon name="userPlus" className="w-4 h-4" />
                Nieuwe ontvanger - voer het volledige e-mailadres in
              </p>
            </div>
          )}
        </div>

        {/* Relationship Selector */}
        <div>
          <label className="block text-sm font-medium text-mv-gray-700 mb-2">
            Relatie
          </label>
          <Select
            value={relationship}
            onChange={(e: ChangeEvent<HTMLSelectElement>) =>
              setRelationship(e.target.value as RelationshipType)
            }
            options={RELATIONSHIP_OPTIONS}
            aria-label="Selecteer relatie met ontvanger"
          />
        </div>

        {/* GAP-025: Permission Level with detailed explanations */}
        <div>
          <label className="block text-sm font-medium text-mv-gray-700 mb-3">
            Toegangsniveau
          </label>
          <div className="space-y-3">
            {PERMISSION_OPTIONS.map((option) => (
              <label
                key={option.value}
                className={`flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-all ${
                  permission === option.value
                    ? 'border-mv-blue-500 bg-mv-blue-50 ring-1 ring-mv-blue-500'
                    : 'border-mv-gray-200 hover:border-mv-gray-300 hover:bg-mv-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name="permission"
                  value={option.value}
                  checked={permission === option.value}
                  onChange={(e) => setPermission(e.target.value as PermissionLevel)}
                  className="mt-1 text-mv-blue-600 focus:ring-mv-blue-500"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Icon name={option.icon as any} className="w-4 h-4 text-mv-gray-500" />
                    <span className="font-medium text-mv-gray-900">{option.label}</span>
                    <Badge className={`text-xs ${option.badgeColor}`}>{option.badge}</Badge>
                    {option.recommended && (
                      <Badge className="text-xs bg-mv-blue-100 text-mv-blue-800">Aanbevolen</Badge>
                    )}
                  </div>
                  <p className="text-sm text-mv-gray-600">{option.description}</p>
                  {option.examples && option.examples.length > 0 && (
                    <p className="text-xs text-mv-gray-500 mt-1 italic">{option.examples[0]}</p>
                  )}
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Expiry Date */}
        <div>
          <label className="block text-sm font-medium text-mv-gray-700 mb-2">
            Vervaldatum (optioneel)
          </label>
          <Select
            value={expiryPreset}
            onChange={(e: ChangeEvent<HTMLSelectElement>) => setExpiryPreset(e.target.value)}
            options={EXPIRY_PRESETS}
            aria-label="Selecteer vervaldatum"
          />
          {expiryPreset === 'custom' && (
            <div className="mt-2">
              <DatePicker
                value={customExpiryDate}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setCustomExpiryDate(e.target.value)}
                min={formatDateForInput(new Date())}
              />
            </div>
          )}
        </div>

        {/* Optional Message */}
        <div>
          <label className="block text-sm font-medium text-mv-gray-700 mb-2">
            Bericht (optioneel)
          </label>
          <Textarea
            value={message}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setMessage(e.target.value)}
            placeholder="Voeg een persoonlijk bericht toe voor de ontvanger..."
            rows={3}
          />
        </div>

        {/* Info Box */}
        <div className="flex gap-3 p-4 bg-mv-blue-50 rounded-lg">
          <Icon name="informationCircle" className="w-5 h-5 text-mv-blue-600 flex-shrink-0" />
          <div className="text-sm text-mv-blue-800">
            <p className="font-medium mb-1">Hoe werkt delen?</p>
            <p>
              De ontvanger krijgt een e-mail met een uitnodiging. Na acceptatie
              krijgt diegene toegang tot {documentCount > 1 ? 'de documenten' : 'het document'} met de door u ingestelde rechten.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-mv-gray-200">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuleren
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            loading={shareDocument.isPending}
          >
            <Icon name="paperAirplane" className="w-4 h-4 mr-2" />
            Uitnodiging Versturen
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default ShareDocumentModal;
